<template>
  <p-row>
    <prona-wizard
      ref="wizard"
      :steps="steps"
      title=""
      :model-class="model"
      :initialize-data="initializeData"
      :service-function="serviceFunction"
      to-on-complete="/"
    ></prona-wizard>
  </p-row>
</template>

<script lang="ts">
  import Wizard, { StepData } from '@/src/components/wizard/prona-wizard.vue';
  import Vue from 'vue';
  import NimSRegistrationRequestViewModel from '@/src/services/v1/viewModel/resource/NimSRegistrationRequestViewModel';
  import NimSContactDataResourceViewModel from '@/src/services/v1/viewModel/resource/NimSContactDataResourceViewModel';
  import NimSContactCommunicationResourceViewModel from '@/src/services/v1/viewModel/resource/NimSContactCommunicationResourceViewModel';
  import AddressViewModel from '@/src/services/v1/viewModel/resource/AddressViewModel';
  import GardenAdviceResourceViewModel from '@/src/services/v1/viewModel/resource/GardenAdviceResourceViewModel';
  import GardenCertificationResourceViewModel from '@/src/services/v1/viewModel/resource/GardenCertificationResourceViewModel';
  import nimsTopicSchema from './1.nims-topic-schema';
  import gardenInformationSchema from './2.garden-information-schema';
  import contactDataSchema from './3.contact-data-schema';
  import nimsOverviewSchema from './4.nims-overview-schema';

  type WizardDataType = InstanceType<typeof Wizard>;

  export default Vue.extend({
    name: 'WizardNimSRegistration',
    layout: 'layout-widget',
    data: () => ({
      title: Vue.$t('pronatura.widget.nimswizard.wizard_title'),
      steps: [] as StepData[],
      model: NimSRegistrationRequestViewModel,
    }),
    async mounted() {
      this.steps = [
        {
          breadcrumb: 'pronatura.widget.nimswizard.breadcrumb.offer_selection',
          title: 'pronatura.widget.nimswizard.offer_selection',
          component: () => import('./1.nims-topic.vue'),
          schema: nimsTopicSchema,
          disablePreviousSteps: true,
        },
        {
          breadcrumb: 'pronatura.widget.nimswizard.breadcrumb.gardenInformation',
          title: 'pronatura.widget.nimswizard.gardenInformation',
          component: () => import('./2.garden-information.vue'),
          schema: gardenInformationSchema,
        },
        {
          breadcrumb: ('pronatura.widget.nimswizard.breadcrumb.contactData'),
          title: 'core.address',
          component: () => import('./3.contact-data.vue'),
          schema: contactDataSchema,
        },
        {
          breadcrumb: 'pronatura.widget.nimswizard.breadcrumb.summary',
          title: 'pronatura.widget.nimswizard.summary',
          component: () => import('./4.nims-overview.vue'),
          schema: nimsOverviewSchema,
          sendData: true,
        },
        {
          breadcrumb: 'pronatura.widget.nimswizard.breadcrumb.confirmation',
          title: 'pronatura.widget.nimswizard.confirmation.title',
          component: () => import('./5.nims-confirmation.vue'),
          disablePreviousSteps: true,
          clearProgressOnMounted: true,
        },
      ];
      const wizard = this.$refs.wizard as WizardDataType;

      wizard.$once('initialized', async (wizardData: NimSRegistrationRequestViewModel) => {
        const nimsTopicKey = wizardData.nimsTopicKeyFromQuery;
        if (this._.isSet(nimsTopicKey)) {
          try {
            await wizard.clearProgress();
            wizardData.natureOffer = nimsTopicKey;
            const currentQuery = this.$routerUtils.getQueryParams();
            delete currentQuery.natureOffer;
            delete this.$route.query.natureOffer;
            this.$routerUtils.updateQueryParams(currentQuery, { preserve: false });
            const selectedTopic = await this.$service.v1.api.widgetNimSTopics.getNimSTopicsById(nimsTopicKey);
            wizardData.natureOffer = selectedTopic.id;

            wizard.saveWizardData();
            wizard.setStep(0);
          } catch (error: any) {
            this.$log.error(error);
            wizard.setStep(0);
          }
        }
      });
    },
    methods: {
      async serviceFunction(wizardData: NimSRegistrationRequestViewModel) {
        await this.$service.v1.api.widgetNimS.createNimSRegistration(wizardData);
      },
      async initializeData() {
        const initializedModel = new NimSRegistrationRequestViewModel().fromModel({
          contactData: new NimSContactDataResourceViewModel(),
          communicationData: new NimSContactCommunicationResourceViewModel(),
          gardenAddress: new AddressViewModel(),
          gardenAdvice: new GardenAdviceResourceViewModel(),
          gardenCertification: new GardenCertificationResourceViewModel(),
        });
        initializedModel.contactData.countryISO = 'CH';
        initializedModel.gardenAddress.countryISO = 'CH';

        return initializedModel;
      },
    },
  });
</script>
